/**
 * Whole-order turnaround for the pre-checkout dialog (owner 2026-07-31).
 *
 * ONE number, not a per-family list: the customer picks up (or receives) the
 * order ONCE, so the only estimate that matters is the slowest family in the
 * cart. The dialog deliberately does not name product families — the owner's
 * call, so nobody reads three rows of irrelevant detail at the moment of
 * payment.
 *
 * Returns null when the pricing has no turnaroundDays block (older published
 * versions) or the cart has no priced prints — in both cases the dialog never
 * shows and checkout proceeds exactly as before.
 */
import { parseSizeId, type PricingData, type TurnaroundRange } from '@rpl/pricing';
import type { UiPrint } from './types';

/** The family a print bills its turnaround under. Framing wins over paper. */
export function turnaroundFamily(p: UiPrint): 'ctype' | 'fineArt' | 'framed' {
  if (p.frameId) return 'framed';
  try {
    return parseSizeId(p.sizeId).family === 'fine_art' ? 'fineArt' : 'ctype';
  } catch {
    return 'ctype';
  }
}

/** Slowest range across the cart, or null when the dialog should not show. */
export function slowestTurnaround(
  pricing: PricingData,
  prints: readonly UiPrint[],
): TurnaroundRange | null {
  const td = pricing.turnaroundDays;
  if (!td) return null;
  let slowest: TurnaroundRange | null = null;
  for (const p of prints) {
    const r = td[turnaroundFamily(p)];
    if (!r || !(r.minDays > 0) || !(r.maxDays >= r.minDays)) continue;
    if (!slowest || r.maxDays > slowest.maxDays ||
        (r.maxDays === slowest.maxDays && r.minDays > slowest.minDays)) {
      slowest = r;
    }
  }
  return slowest;
}

/** "5-7 days" / "3 days" — the dialog's headline fragment. */
export function turnaroundLabel(r: TurnaroundRange): string {
  return r.minDays === r.maxDays ? `${r.maxDays} days` : `${r.minDays}-${r.maxDays} days`;
}
